import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { getAllCustomers } from "../../../services/customerService";
import { Customer } from "../../../types/customer";
import { debug } from "../../../utils/debugLogger";
import { validateOAuthState } from "../../../utils/oauthState";
import {
  exchangeGHLToken,
  GHL_OAUTH_STATE_OPTIONS,
  GHL_OAUTH_STATE_PROVIDER,
} from "../services/ghlAuthentication";

/**
 * Recover the Cognito token after the GoHighLevel redirect wipes React context.
 * Priority: live AuthContext → ghl_pending_token → generic idToken.
 * Same helper as WordPressCallback/LinkedInCallback — this route is public for
 * the same reason theirs are: AuthContext is not reliably rehydrated at the
 * moment the provider redirects back, and a ProtectedRoute would bounce the user
 * to /login and discard the authorization code.
 */
const resolveToken = (authIdToken: string): string => {
  if (authIdToken) return authIdToken;
  const pending = localStorage.getItem("ghl_pending_token");
  if (pending) return pending;
  return localStorage.getItem("idToken") ?? "";
};

const GHLCallback = () => {
  const navigate = useNavigate();
  const { idToken: authIdToken, isLoading, isAuthenticated } = useAuth();
  const idToken = resolveToken(authIdToken);

  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [customersList, setCustomersList] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [connectedLocationId, setConnectedLocationId] = useState<string | null>(null);
  // An OAuth authorization code is single-use. Once we have POSTed it to the
  // backend, GoHighLevel has consumed it whether or not the rest of the exchange
  // succeeded — so pressing "Connect" again can only ever fail with an opaque
  // "invalid code". Any failure past that point requires starting over.
  const [mustRestart, setMustRestart] = useState(false);

  // ── Step 1: extract the authorization code ────────────────────────────────
  useEffect(() => {
    if (isLoading) return;
    const urlParams = new URLSearchParams(window.location.search);
    const urlCode = urlParams.get("code");
    const errorParam = urlParams.get("error");
    const errorDescription = urlParams.get("error_description");
    const returnedState = urlParams.get("state");

    // GHL's docs do not guarantee `state` is echoed back, so a missing value is
    // tolerated here rather than failing the connect outright. This is weaker
    // than the other platforms on purpose, and it is not the only defence: the
    // backend exchange requires a Cognito token AND tenant ownership of the
    // customer_id, so a stolen code cannot be planted into another account.
    // If GHL is confirmed to round-trip state, drop the null branch below.
    if (!errorParam && returnedState !== null) {
      if (
        !validateOAuthState(
          GHL_OAUTH_STATE_PROVIDER,
          returnedState,
          GHL_OAUTH_STATE_OPTIONS,
        )
      ) {
        setError(
          "Security check failed (invalid OAuth state). Please try connecting again."
        );
        return;
      }
    } else if (!errorParam) {
      debug.warn("GHL did not return an OAuth state parameter");
    }

    if (errorParam) {
      // Log the provider's raw error but show the user something actionable.
      debug.error("GHL OAuth error:", errorParam, errorDescription);
      setError(
        errorParam === "access_denied"
          ? "GoHighLevel connection was cancelled. You can connect whenever you're ready."
          : "We couldn't connect your GoHighLevel account. Please try again."
      );
      return;
    }
    if (!urlCode) {
      setError("No authorization code received. Please try connecting again.");
      return;
    }
    setCode(urlCode);
  }, [isLoading]);

  // ── Step 2: load the business profiles to attach this sub-account to ──────
  useEffect(() => {
    if (!idToken || !code) return;
    let active = true;
    setCustomersLoading(true);
    getAllCustomers(idToken)
      .then((customers) => {
        if (active) setCustomersList(customers);
      })
      .catch((err) => debug.error("Error fetching customers:", err))
      .finally(() => {
        if (active) setCustomersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [idToken, code]);

  // ── Step 3: exchange the code on the backend ──────────────────────────────
  const handleConnect = async () => {
    if (!code || !selectedCustomerId) {
      setError("Please select a business profile to continue.");
      return;
    }
    const currentToken = resolveToken(authIdToken);
    if (!currentToken) {
      setError("Please log in first, then connect GoHighLevel.");
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      const response = await exchangeGHLToken(code, selectedCustomerId, currentToken);
      localStorage.removeItem("ghl_pending_token");
      setConnectedLocationId(response.location_id);
    } catch (err: any) {
      // The backend's messages are written to be shown ("choose a sub-account",
      // "already connected to another account") — surface them rather than a
      // generic failure, since they tell the user what to actually do.
      setError(
        err?.response?.data?.error ||
          (err instanceof Error ? err.message : null) ||
          "Failed to connect GoHighLevel."
      );
      setMustRestart(true);
    } finally {
      setIsSaving(false);
    }
  };

  const goToConnect = () => navigate("/social-posts?tab=connect");

  if (!isLoading && !isAuthenticated && !localStorage.getItem("ghl_pending_token")) {
    navigate("/login", { state: { returnUrl: "/social-posts?tab=connect" } });
    return null;
  }

  const BackButton = () => (
    <div className="w-full flex justify-start pl-4 mb-2">
      <button
        type="button"
        onClick={goToConnect}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        Back
      </button>
    </div>
  );

  // ── Error / cancelled before we ever got a code ───────────────────────────
  if (error && !code) {
    return (
      <div className="flex flex-col min-h-screen p-4 bg-theme-background text-theme-fg">
        <BackButton />
        <div className="flex flex-col items-center justify-center flex-1">
          <div className="w-full max-w-md bg-theme-surface rounded-2xl shadow-sm border border-theme-border p-8 text-center">
            <h2 className="mb-1.5 text-lg font-semibold text-theme-fg">
              GoHighLevel not connected
            </h2>
            <p className="mb-6 text-sm text-theme-fg-muted">{error}</p>
            <button
              type="button"
              onClick={goToConnect}
              className="rounded-lg bg-theme-primary px-5 py-2.5 font-semibold text-white transition hover:opacity-90"
            >
              Back to Connections
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen p-4">
        <BackButton />
        <div className="flex flex-col items-center justify-center flex-1">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen p-4">
      <BackButton />
      <div className="flex flex-col items-center w-full flex-1">
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
          <header className="mb-8 pb-8 border-b border-gray-100">
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
              Connect GoHighLevel
            </h1>
            <p className="text-sm text-gray-500">
              Link your GoHighLevel sub-account to a business profile.
            </p>
          </header>

          {connectedLocationId ? (
            <>
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <p className="font-semibold text-gray-900">GoHighLevel sub-account</p>
                <p className="text-xs text-gray-500 break-all mt-0.5">
                  Location ID: {connectedLocationId}
                </p>
                <p className="text-sm text-green-700 font-medium mt-1">Connected</p>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={goToConnect}
                  className="px-4 py-2 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-900"
                >
                  Done
                </button>
              </div>
            </>
          ) : code ? (
            <>
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-gray-700">
                  GoHighLevel sub-account authorized
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Select the business profile this sub-account belongs to.
                </p>
              </div>
              {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
              <div className="max-w-xl mb-6">
                <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col shadow-sm">
                  {mustRestart ? (
                    /* The authorization code has been spent — retrying it here
                       can only produce a confusing "invalid code" error. */
                    <div className="space-y-3">
                      <p className="text-xs text-gray-600">
                        This connection attempt has expired. Please start the
                        connection again from the Connections page.
                      </p>
                      <button
                        type="button"
                        onClick={goToConnect}
                        className="w-full py-2 text-xs text-white bg-brand-primary rounded-lg font-semibold hover:bg-brand-primary-hover transition-all shadow-sm"
                      >
                        Start over
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                          Assign Profile
                        </label>
                        <select
                          value={selectedCustomerId}
                          onChange={(e) => setSelectedCustomerId(e.target.value)}
                          disabled={customersLoading || isSaving}
                          className="block w-full rounded-lg border border-gray-200 text-xs h-9 px-3 focus:ring-brand-primary focus:border-brand-primary"
                        >
                          <option value="">-- Select a Business Profile --</option>
                          {customersList.map((c) => (
                            <option key={c.customer_id} value={c.customer_id}>
                              {c.business_name}
                              {c.city && c.state ? ` - ${c.city}, ${c.state}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={handleConnect}
                        disabled={!selectedCustomerId || isSaving}
                        className="w-full py-2 text-xs text-white bg-brand-primary rounded-lg font-semibold hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                      >
                        {isSaving ? (
                          <span className="flex items-center justify-center gap-1">
                            <span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                            Connecting...
                          </span>
                        ) : (
                          "Connect"
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={goToConnect}
                  className="px-4 py-2 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-900"
                >
                  Done
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default GHLCallback;
